import type { BlacklistAutoRuleId } from '@hprofits/shared';
import {
  BlacklistAutoRuleIdSchema,
  DictBlacklistBanTypeIdSchema,
  DictBlacklistStatusIdSchema,
  NumericIdSchema,
} from '@hprofits/shared';
import { useConfirm } from '@hprofits/ui';
import isEqual from 'lodash/isEqual';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref, shallowRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { z } from 'zod';

import { t } from '@/i18n';
import { useFormProducer } from '@/modules/common/state/useFormController';

import type { BlacklistRuleForm, ConditionRow } from '../../../../api/blacklistAutoRuleContract';
import { emptyBlacklistForm, toBlacklistForm, toConditionRows } from '../../../../api/blacklistAutoRuleContract';
import type { BlacklistDictionariesQuery } from '../../../../api/useBlacklistDictionaries';
import { useBlacklistDictionaries } from '../../../../api/useBlacklistDictionaries';
import { useBlacklistEditorQueries } from '../../../../api/useBlacklistEditorQueries';
import { checkPeriodMinSchema } from '../../../../utils/checkPeriodMin';
import { readBlacklistErrorMessage } from '../../../../utils/readBlacklistErrorMessage';
import type { BlacklistEditorConditions, BlacklistEditorFields, BlacklistEditorForm } from '../context/types';
import { useConditionTable } from './useConditionTable';
import { useEditorFields } from './useEditorFields';

export type BlacklistEditorQueries = ReturnType<typeof useBlacklistEditorQueries> &
  Readonly<{ dictionaries: BlacklistDictionariesQuery }>;

export type BlacklistEditorState = Readonly<{
  id: ComputedRef<BlacklistAutoRuleId | null>;
  title: ComputedRef<string>;
  queries: BlacklistEditorQueries;
  form: BlacklistEditorForm;
  /** The name rule on its own, so a rejected save can tell whether the heading is what failed. */
  nameSchema: z.ZodType<string>;
  fields: BlacklistEditorFields;
  conditions: BlacklistEditorConditions;
  conditionError: Ref<string>;
  nameError: Ref<string>;
  loading: ComputedRef<boolean>;
  loadError: ComputedRef<boolean>;
  loadErrorMessage: ComputedRef<string | undefined>;
  ready: ComputedRef<boolean>;
  isDirty: ComputedRef<boolean>;
  /** Makes `rows` the condition baseline that `discard` returns to. */
  seedConditions: (rows: ReadonlyArray<ConditionRow>) => void;
  restoreSeed: () => void;
  setBanAdUnitTypes: (ids: Array<number>) => Promise<void>;
}>;

export const useBlacklistEditorState = (): BlacklistEditorState => {
  const route = useRoute();
  const confirm = useConfirm();
  // `safeParse`: a malformed `:id` (typo, stale bookmark) must surface as a load error, not throw
  // out of the layout's setup and blank the whole editor.
  const parsedId = computed(() =>
    route.params.id ? BlacklistAutoRuleIdSchema.safeParse(Number(route.params.id)) : null,
  );
  const id = computed(() => (parsedId.value?.success ? parsedId.value.data : null));
  const invalidId = computed(() => parsedId.value !== null && !parsedId.value.success);
  const queries: BlacklistEditorQueries = {
    ...useBlacklistEditorQueries(id),
    dictionaries: useBlacklistDictionaries(),
  };
  // Named separately from the object schema because `name` is the one field with no control inside
  // either SmartForm card — it is edited through the page heading — so its per-field error has
  // nowhere to render and a rejected save has to re-check it here to point the user at the heading.
  const nameSchema = z
    .string()
    .refine((name) => name.trim().length > 0, { error: () => t('modules.blacklistAutoRules.editor.required') });
  const schema = z.object({
    name: nameSchema,
    type: DictBlacklistBanTypeIdSchema.nullable(),
    actionState: DictBlacklistStatusIdSchema.nullable(),
    checkPeriodMin: checkPeriodMinSchema(),
    adUnitIds: z.array(NumericIdSchema),
    excludeAdUnitIds: z.array(NumericIdSchema),
    banAdUnitTypeIds: z.array(z.number().int().nonnegative()),
  });
  const form = useFormProducer<BlacklistRuleForm>({
    defaults: emptyBlacklistForm(),
    schema,
    toSubmit: (values) => values,
  });
  // Included units stay next to the types (they reach Internal-SSP ad units the type ban skips).
  // Removing the last type must either clear its exclusions or restore the type set so the rule
  // stays valid.
  const setBanAdUnitTypes = async (ids: Array<number>) => {
    const previousIds = form.state.formData.value.banAdUnitTypeIds;
    form.form.setFieldValue('banAdUnitTypeIds', ids);
    const { excludeAdUnitIds } = form.state.formData.value;
    if (ids.length === 0 && excludeAdUnitIds.length > 0) {
      const dropExclusions = await confirm({
        title: t('modules.blacklistAutoRules.editor.clearExclusionsTitle'),
        description: t('modules.blacklistAutoRules.editor.clearExclusionsDescription', {
          count: excludeAdUnitIds.length,
        }),
        confirmText: t('modules.blacklistAutoRules.editor.clearExclusions'),
        cancelText: t('modules.blacklistAutoRules.editor.keepTypes'),
      });
      if (dropExclusions) {
        form.form.setFieldValue('excludeAdUnitIds', []);
      } else {
        form.form.setFieldValue('banAdUnitTypeIds', previousIds);
      }
    }
  };
  const conditions = useConditionTable(queries.dictionaries);
  const fields = useEditorFields(queries.dictionaries);
  const conditionError = ref('');
  const nameError = ref('');
  watch(
    () => form.state.formData.value.name,
    (next) => {
      if (nameError.value !== '' && nameSchema.safeParse(next).success) {
        nameError.value = '';
      }
    },
  );
  // Save raises the error; while it shows, it follows the rows — gone once they are fixed, or
  // switched to whatever problem is left — without another Save.
  watch(
    () => conditions.table.rows.value,
    () => {
      if (conditionError.value !== '') {
        conditionError.value = conditions.check().error ?? '';
      }
    },
  );
  const initializedId = ref<number | null>(null);
  // Last seeded condition rows: `restoreSeed` returns to them alongside the form baseline.
  const seedRows = shallowRef<ReadonlyArray<ConditionRow>>([]);
  const seedConditions = (rows: ReadonlyArray<ConditionRow>): void => {
    seedRows.value = rows;
    conditions.table.setRows(rows, { source: 'seed' });
    conditions.table.resetBaseline();
  };
  const restoreSeed = (): void => {
    form.reset(form.baseline.value);
    seedConditions(seedRows.value);
  };
  const isFormDirty = computed(() => !isEqual(form.state.formData.value, form.baseline.value));
  const isDirty = computed(() => isFormDirty.value || conditions.table.hasDirtyRows.value);
  watch(
    queries.rule.data,
    (rule) => {
      // A revisit of the SAME id can serve a stale cached response before the background refetch
      // (triggered by the query's own invalidation) resolves — the first (stale) emission already
      // satisfies `initializedId.value !== rule.id` and seeds the form, so a plain one-shot guard
      // would ignore the second (fresh) emission forever. Reseeding whenever nothing is locally
      // dirty yet — not just on an id change — picks up that later, correct emission too, while
      // still never clobbering an in-progress edit.
      if (rule && (initializedId.value !== rule.id || !isDirty.value)) {
        form.reset(toBlacklistForm(rule));
        seedConditions(toConditionRows(rule));
        initializedId.value = rule.id;
      }
    },
    { immediate: true },
  );
  // Both editor routes share one layout instance, so `/:id` → `/new` does not remount: reseed the
  // empty form explicitly or the previous rule's values would survive into "Create".
  watch(id, (next) => {
    if (next === null && initializedId.value !== null) {
      form.reset(emptyBlacklistForm());
      seedConditions([]);
      initializedId.value = null;
    }
  });
  const title = computed(() =>
    parsedId.value === null
      ? t('modules.blacklistAutoRules.editor.createTitle')
      : t('modules.blacklistAutoRules.editor.editTitle', { id: id.value ?? String(route.params.id) }),
  );
  const loading = computed(
    () => queries.dictionaries.isPending.value || (id.value !== null && queries.rule.isPending.value),
  );
  const loadError = computed(
    () => invalidId.value || queries.dictionaries.isError.value || (id.value !== null && queries.rule.isError.value),
  );
  // The rule's own error first: a missing or broken rule is what the user opened the page for.
  const loadErrorMessage = computed(() =>
    readBlacklistErrorMessage(queries.rule.error.value ?? queries.dictionaries.error.value),
  );
  const ready = computed(() => !loading.value && !loadError.value);

  return {
    id,
    title,
    queries,
    form,
    nameSchema,
    fields,
    conditions,
    conditionError,
    nameError,
    loading,
    loadError,
    loadErrorMessage,
    ready,
    isDirty,
    seedConditions,
    restoreSeed,
    setBanAdUnitTypes,
  };
};
